import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@diana/shared'

/**
 * URL is the source of truth for the language: `/` is Polish, `/uk/...` and
 * `/ru/...` are prefixed. The server's meta injection (apps/api/src/ssr/meta.ts)
 * parses the same prefixes, so SEO and the SPA can never disagree.
 *
 * localStorage only remembers the visitor's choice so the *homepage* can greet
 * a returning Ukrainian visitor in Ukrainian. Deep links never redirect — a
 * shared Polish link must open in Polish.
 */

const STORAGE_KEY = 'diana.locale'

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE)

export function useLocale(): Locale {
  return useContext(LocaleContext)
}

/** `/uk` + `/kontakt` -> `/uk/kontakt`; the default locale stays unprefixed. */
export function localePath(locale: Locale, path: string): string {
  if (locale === DEFAULT_LOCALE) return path
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

/** Locale-aware link builder: `const to = usePath(); to('/kontakt')`. */
export function usePath(): (path: string) => string {
  const locale = useLocale()
  return (path: string) => localePath(locale, path)
}

function storedLocale(): Locale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return LOCALES.includes(raw as Locale) ? (raw as Locale) : null
  } catch {
    return null
  }
}

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Captured during the first render, BEFORE the write effect below runs —
  // otherwise landing on `/` would store 'pl' and erase the preference the
  // redirect is about to read.
  const [remembered] = useState(storedLocale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      /* private mode — fine */
    }
  }, [locale])

  // Greet returning visitors in their language — on the bare homepage only.
  // Deep links never redirect: a shared Polish link must open in Polish.
  useEffect(() => {
    if (pathname !== '/') return
    if (remembered && remembered !== DEFAULT_LOCALE) {
      navigate(localePath(remembered, '/'), { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
}

/**
 * A language for one part of a page, with none of LocaleProvider's side
 * effects (no <html lang>, nothing remembered). Diana's teacher view uses it
 * to show a lesson in her student's language.
 */
export function LocaleScope({ locale, children }: { locale: Locale; children: ReactNode }) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
}

/** Strip the locale prefix: `/uk/kontakt` -> `/kontakt`. Used by the switcher. */
export function pathWithoutLocale(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] && LOCALES.includes(parts[0] as Locale)) {
    const rest = parts.slice(1).join('/')
    return rest ? `/${rest}` : '/'
  }
  return pathname || '/'
}
